import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiUsers, FiTarget, FiTrendingUp, FiActivity, FiCalendar, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getAssessmentResults, AssessmentResult, AssessmentResultsResponse } from '../../api/admin';
import { TRACK_NAMES, CQPM_TARGETS_BY_TRACK } from '../../types/constants';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';

// Helper function to convert Date to local YYYY-MM-DD string
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const AssessmentResultsHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Assessment Results Dashboard
    </span>
  </h1>
);

// Date range selector component
interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onSearch: () => void;
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false
}) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onSearch}
              disabled={isLoading || !startDate || !endDate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              <FiCalendar className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button
              onClick={onRefresh}
              disabled={isLoading || !startDate || !endDate}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalAssessments: number;
  uniqueUsers: number;
  avgSpeedScore: number;
  topSpeedScore: number;
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalAssessments,
  uniqueUsers,
  avgSpeedScore,
  topSpeedScore,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                <div className="h-8 bg-gray-300 rounded mb-2"></div>
                <div className="h-3 bg-gray-300 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Assessments',
      value: totalAssessments.toLocaleString(),
      icon: FiActivity,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Unique Users',
      value: uniqueUsers.toLocaleString(),
      icon: FiUsers,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Avg Speed Score',
      value: avgSpeedScore.toFixed(1),
      icon: FiTarget,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Top Speed Score',
      value: topSpeedScore.toFixed(1),
      icon: FiTrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((stat, index) => (
        <Card key={index} className="shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>
                  {stat.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Assessment results table component
interface AssessmentResultsTableProps {
  assessments: AssessmentResult[];
  attemptsMap: Map<string, number>;
  increaseMap: Map<string, number>;
  isLoading?: boolean;
}

const AssessmentResultsTable: React.FC<AssessmentResultsTableProps> = ({
  assessments,
  attemptsMap,
  increaseMap,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            No completed assessments found for the selected date range.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Speed Score (CQPM)
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Track Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grade
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Attempts
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Increase
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assessments.map((assessment, index) => {
                const trackTarget = CQPM_TARGETS_BY_TRACK[assessment.trackId as keyof typeof CQPM_TARGETS_BY_TRACK] || 30;
                const trackName = TRACK_NAMES[assessment.trackId as keyof typeof TRACK_NAMES] || assessment.trackId;
                const attemptKey = `${assessment.userEmail}-${assessment.trackId}`;
                const attempts = attemptsMap.get(attemptKey) || 1;
                const increase = increaseMap.get(attemptKey) || 0;
                
                return (
                  <tr key={`${assessment.userEmail}-${assessment.date}-${assessment.trackId}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {assessment.userEmail}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        assessment.speedScore >= trackTarget 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {assessment.speedScore}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(assessment.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {trackName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {assessment.ageGrade !== null && assessment.ageGrade !== undefined ? assessment.ageGrade : 'None'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {attempts}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        increase > 0 
                          ? 'bg-blue-100 text-blue-800' 
                          : increase === 0 
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {increase > 0 ? `+${increase}` : increase}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const AssessmentResults: React.FC = () => {
  const { user: currentUser } = useAuth();
  
  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // State
  const [startDate, setStartDate] = useState(() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 2);
    return formatDateLocal(startDate);
  });
  const [endDate, setEndDate] = useState(() => formatDateLocal(new Date()));
  const [data, setData] = useState<AssessmentResultsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate summary stats from data and attempts per user-track combination
  const { summaryStats, attemptsMap, increaseMap } = useMemo(() => {
    if (!data || !data.assessments) {
      return { 
        summaryStats: { totalAssessments: 0, uniqueUsers: 0, avgSpeedScore: 0, topSpeedScore: 0 },
        attemptsMap: new Map<string, number>(),
        increaseMap: new Map<string, number>()
      };
    }

    const assessments = data.assessments;
    const uniqueUsers = new Set(assessments.map(a => a.userEmail)).size;
    const avgSpeedScore = assessments.length > 0 
      ? assessments.reduce((sum, a) => sum + a.speedScore, 0) / assessments.length 
      : 0;
    const topSpeedScore = assessments.length > 0 
      ? Math.max(...assessments.map(a => a.speedScore)) 
      : 0;

    // Calculate attempts and score ranges per user-track combination
    const attemptsMap = new Map<string, number>();
    const increaseMap = new Map<string, number>();
    const scoreTracker = new Map<string, number[]>(); // Track all scores per user-track

    assessments.forEach(assessment => {
      const key = `${assessment.userEmail}-${assessment.trackId}`;
      
      // Count attempts
      attemptsMap.set(key, (attemptsMap.get(key) || 0) + 1);
      
      // Track all scores for this user-track combination
      if (!scoreTracker.has(key)) {
        scoreTracker.set(key, []);
      }
      scoreTracker.get(key)!.push(assessment.speedScore);
    });

    // Calculate increase (best - worst) for each user-track combination
    scoreTracker.forEach((scores, key) => {
      if (scores.length > 1) {
        const bestScore = Math.max(...scores);
        const worstScore = Math.min(...scores);
        increaseMap.set(key, bestScore - worstScore);
      } else {
        increaseMap.set(key, 0); // Single attempt = no increase
      }
    });

    return {
      summaryStats: {
        totalAssessments: assessments.length,
        uniqueUsers,
        avgSpeedScore,
        topSpeedScore
      },
      attemptsMap,
      increaseMap
    };
  }, [data]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getAssessmentResults(startDate, endDate);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch assessment results';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!data?.assessments || data.assessments.length === 0) return;

    // Define CSV headers based on table columns
    const headers = [
      { key: 'userEmail' as keyof AssessmentResult, label: 'User Email' },
      { key: 'speedScore' as keyof AssessmentResult, label: 'Speed Score (CQPM)' },
      { key: 'date' as keyof AssessmentResult, label: 'Date' },
      { key: 'trackId' as keyof AssessmentResult, label: 'Track Name' },
      { key: 'ageGrade' as keyof AssessmentResult, label: 'Grade' },
      { key: 'attempts' as keyof (AssessmentResult & { attempts: number; increase: number }), label: 'Attempts' },
      { key: 'increase' as keyof (AssessmentResult & { attempts: number; increase: number }), label: 'Increase' }
    ];

    // Transform data for CSV with formatted track names and calculated values
    const csvData = data.assessments.map(assessment => {
      const trackName = TRACK_NAMES[assessment.trackId as keyof typeof TRACK_NAMES] || assessment.trackId;
      const attemptKey = `${assessment.userEmail}-${assessment.trackId}`;
      const attempts = attemptsMap.get(attemptKey) || 1;
      const increase = increaseMap.get(attemptKey) || 0;
      
      return {
        ...assessment,
        trackId: trackName,
        date: new Date(assessment.date).toLocaleDateString(),
        ageGrade: assessment.ageGrade !== null && assessment.ageGrade !== undefined ? assessment.ageGrade : 'None',
        attempts,
        increase
      };
    });

    const csvContent = convertToCSV(csvData, headers);
    const filename = `assessment-results-${startDate}-to-${endDate}-${formatDateForFilename()}.csv`;
    
    downloadCSV(csvContent, filename);
  }, [data, startDate, endDate, attemptsMap, increaseMap]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <AssessmentResultsHeader />
        
        {/* Navigation */}
        <div className="flex mb-4">
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Date Range Selector */}
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onSearch={fetchData}
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={!!(data?.assessments && data.assessments.length > 0)}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalAssessments={summaryStats.totalAssessments}
              uniqueUsers={summaryStats.uniqueUsers}
              avgSpeedScore={summaryStats.avgSpeedScore}
              topSpeedScore={summaryStats.topSpeedScore}
              isLoading={isLoading}
            />

            {/* Assessment Results Table */}
            <AssessmentResultsTable
              assessments={data?.assessments || []}
              attemptsMap={attemptsMap}
              increaseMap={increaseMap}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};